import type { AccountType, ClientStatus, UserStatus } from "./types/users.types";

/**
 * The one palette a user's status is drawn in, wherever the row appears — so
 * the same status never arrives in two different colours. "Ending soon" and
 * "Active" are carried over from the dietitian app unchanged, so a status
 * means the same thing and looks the same in both consoles.
 *
 * Read as a column the hues are the point: red is the only one asking for
 * anything, orange is a trial that went nowhere, the two blues are an account
 * still early in its life, and teal is the settled case. The blues are close
 * to each other on purpose — "Signed up" and "Trial active" are neighbouring
 * moments in the same story, and pulling them apart would imply a difference
 * of kind rather than of stage.
 */
export const USER_STATUS_STYLES: Record<UserStatus, string> = {
  "Ending soon": "bg-[#FDECEC] text-[#D14343]",
  "Trial ended": "bg-[#FDF0E3] text-[#C2762A]",
  "Trial active": "bg-[#DCE3FA] text-[#3B4FA6]",
  "Signed up": "bg-[#EAEFFC] text-[#4A63C4]",
  Active: "bg-brand-soft text-[#00899B]",
};

/**
 * The palette a client's status is drawn in, on the detail screen's roster.
 *
 * Teal for the live one, as everywhere else in the panel. Grey rather than red
 * for the other: a client who has stopped coming is a fact about somebody
 * else's practice, not a problem on this screen, and red is reserved for the
 * rows that ask for something.
 */
export const CLIENT_STATUS_STYLES: Record<ClientStatus, string> = {
  Active: "bg-brand-soft text-[#00899B]",
  Inactive: "bg-[#F1F4F6] text-ink-muted",
};

/**
 * What the Account type filter offers, in the order it offers it.
 *
 * Four, one per value the roster endpoint's `plan` parameter takes. There was
 * a "Paid" option between the trial and the two cycles; it went when the list
 * moved to the server, which has no such grouping to ask for — see
 * `AccountType`.
 *
 * There is no `planMatchesAccountType` beside this any more either. It existed
 * to narrow the fixtures the way the server narrows the roster, and the roster
 * is the server's to narrow now.
 */
export const ACCOUNT_TYPE_OPTIONS: { value: AccountType; label: string }[] = [
  { value: "any", label: "All account types" },
  { value: "trial", label: "Free trial" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];
